/**
 * Thin persistence helper.
 *
 * Uses expo-secure-store on device (encrypted) and localStorage on web,
 * where SecureStore is unavailable.
 */

import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

/**
 * How the keychain guards everything we store.
 *
 * NOT expo's `WHEN_UNLOCKED` default. Under that, a keychain item is readable
 * only while the phone is unlocked — and the stay-visible background task wakes
 * precisely when it is not. It read nothing, returned early, and the user
 * silently stopped being visible: the one situation the whole feature exists
 * for. Nothing in the app said so, because a locked read fails the same way an
 * empty one does.
 *
 * `AFTER_FIRST_UNLOCK` is Apple's answer for credentials a background task
 * needs: still encrypted at rest, still unreadable until the owner has unlocked
 * the device at least once since boot, so a phone seized cold gives nothing up.
 * `THIS_DEVICE_ONLY` keeps a session token out of an iCloud keychain restore
 * onto a different handset — it is a bearer credential, not a preference.
 *
 * iOS only. Android's keystore has no lock-state equivalent and ignores this.
 */
const KEYCHAIN: SecureStore.SecureStoreOptions = {
  keychainAccessible: SecureStore.AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY,
};

export const storage = {
  async get(key: string): Promise<string | null> {
    if (Platform.OS === "web") {
      try {
        return globalThis.localStorage?.getItem(key) ?? null;
      } catch {
        return null;
      }
    }

    try {
      return await SecureStore.getItemAsync(key, KEYCHAIN);
    } catch (err) {
      // A read that fails — keychain unavailable, the native module missing
      // from a stale build, a corrupt entry — reads as "nothing stored". It
      // must never reject: reads happen on the launch path (language, session),
      // where an unhandled rejection leaves the app on the splash screen with
      // nothing on screen to say why.
      console.warn(`[storage] unable to read "${key}"`, err);
      return null;
    }
  },

  async set(key: string, value: string): Promise<void> {
    if (Platform.OS === "web") {
      try {
        globalThis.localStorage?.setItem(key, value);
      } catch {
        /* ignore */
      }
      return;
    }
    await SecureStore.setItemAsync(key, value, KEYCHAIN);
  },

  async remove(key: string): Promise<void> {
    if (Platform.OS === "web") {
      try {
        globalThis.localStorage?.removeItem(key);
      } catch {
        /* ignore */
      }
      return;
    }
    await SecureStore.deleteItemAsync(key, KEYCHAIN);
  },
};

/** Marks that the keys below have been rewritten under the new accessibility. */
const RESEAL_KEY = "opncue.keychain.seal";
const RESEAL_VERSION = "after-first-unlock";

/**
 * Rewrite existing keychain items so they actually pick up `KEYCHAIN` above.
 *
 * Setting the option is not enough on its own. `kSecAttrAccessible` is written
 * by `SecItemAdd` and never touched again: expo's `setItemAsync` falls back to
 * `SecItemUpdate` when the key already exists, and that update carries only the
 * value. So an item first written under `WHEN_UNLOCKED` keeps that guard for
 * life, however many times it is rewritten — and iOS keeps keychain items when
 * an app is deleted, so even a reinstall inherits the old one. Without this, the
 * fix above would work on a fresh device and quietly do nothing on any phone
 * that had already run the app.
 *
 * Delete-then-write is the only way to reset the attribute, so it is done once,
 * from the foreground, holding the value in memory throughout. If any part
 * fails the marker is not written and the next launch tries again.
 */
export async function resealKeychain(keys: string[]): Promise<void> {
  if (Platform.OS === "web") return;
  try {
    if ((await storage.get(RESEAL_KEY)) === RESEAL_VERSION) return;
    for (const key of keys) {
      const value = await storage.get(key);
      if (value === null) continue;
      await storage.remove(key);
      await storage.set(key, value);
    }
    await storage.set(RESEAL_KEY, RESEAL_VERSION);
  } catch (err) {
    // Best effort: a phone that fails here keeps the old accessibility and
    // retries next launch. Never block the launch path on it.
    console.warn("[storage] keychain reseal failed", err);
  }
}
